import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiLock, FiUser, FiEye, FiEyeOff } = FiIcons;

export default function Login() {
  const [accessCode, setAccessCode] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  
  const { login } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    
    // Simuliere Login-Verzögerung
    await new Promise(resolve => setTimeout(resolve, 800));
    
    const success = login(accessCode);
    if (!success) {
      setError('Zugangsdaten ungültig');
    }
    
    setIsLoading(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center p-4">
      <motion.div 
        initial={{ opacity: 0, y: 20 }} 
        animate={{ opacity: 1, y: 0 }} 
        className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-md"
      >
        <div className="text-center mb-8">
          <div className="bg-gradient-to-r from-[#9b061b] to-[#1e314b] w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <SafeIcon icon={FiLock} className="text-white text-2xl" />
          </div>
          <h1 className="text-2xl font-bold text-[#1e314b] mb-2">CBUY.EXPERT</h1>
          <p className="text-gray-600">Energievertrieb</p>
          <p className="text-sm text-gray-500 mt-2">Nur für autorisierte Handelsvertreter</p>
        </div>
        
        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Zugangscode</label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <SafeIcon icon={FiUser} className="text-gray-400" />
              </div>
              <input 
                type={showPassword ? 'text' : 'password'} 
                value={accessCode} 
                onChange={(e) => setAccessCode(e.target.value)} 
                className="block w-full pl-10 pr-10 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#9b061b] focus:border-[#9b061b] transition-colors" 
                placeholder="Zugangscode eingeben..." 
                required 
              />
              <button 
                type="button" 
                onClick={() => setShowPassword(!showPassword)} 
                className="absolute inset-y-0 right-0 pr-3 flex items-center"
              >
                <SafeIcon icon={showPassword ? FiEyeOff : FiEye} className="text-gray-400 hover:text-gray-600 transition-colors" />
              </button>
            </div>
          </div>
          
          {error && (
            <motion.div 
              initial={{ opacity: 0, scale: 0.95 }} 
              animate={{ opacity: 1, scale: 1 }} 
              className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm" 
            >
              {error}
            </motion.div>
          )}
          
          <motion.button 
            whileHover={{ scale: 1.02 }} 
            whileTap={{ scale: 0.98 }} 
            type="submit" 
            disabled={isLoading} 
            className="w-full bg-gradient-to-r from-[#9b061b] to-[#1e314b] text-white py-3 px-4 rounded-lg font-medium hover:shadow-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed" 
          >
            {isLoading ? 'Anmelden...' : 'Login'}
          </motion.button>
        </form>
        
        {/* Demo-Codes nur im Development-Modus anzeigen */}
        {process.env.NODE_ENV === 'development' && (
          <div className="mt-8 pt-6 border-t border-gray-200 text-center">
            <p className="text-xs text-gray-500 mb-2">Demo-Zugangscodes (nur Development):</p>
            <div className="space-y-1 text-xs">
              <p className="text-gray-600">
                <span className="font-mono bg-gray-100 px-2 py-1 rounded">CBUY-USER-2024</span> (Handelsvertreter)
              </p>
              <p className="text-gray-600">
                <span className="font-mono bg-gray-100 px-2 py-1 rounded">CBUY-ADMIN-2024</span> (Administrator)
              </p>
            </div>
          </div>
        )}
      </motion.div>
    </div>
  );
}